import { useCallback, useEffect, useRef, useState } from 'react';
import { useToken } from '@/common/helper';
import { isRefusedRequest } from '@/common/interceptor/interceptor';
import { getWorkflowHistory } from '@/apis/logiblocksAPI';
import type {
  WorkflowRunHistoryDetail,
  WorkflowRunStatus,
} from '@/apis/logiblocksAPI';

// How long after a RUNNING response, or one of the first SUSPENDED responses, the
// selected run is fetched again.
export const RUN_DETAIL_POLL_MS = 3000;
// How long after a later SUSPENDED response the selected run is fetched again. A
// suspension past the first few polls is usually a long wait, such as a delay node.
export const SUSPENDED_RUN_DETAIL_POLL_MS = 20000;
// How many consecutive SUSPENDED responses are followed by a `RUN_DETAIL_POLL_MS`
// poll, which catches a suspension that ends almost at once.
export const FAST_SUSPENDED_POLL_COUNT = 2;

/**
 * Returns how long to wait before fetching the run again, or null to stop.
 * `suspendedResponseCount` counts the run's consecutive SUSPENDED responses,
 * including this one.
 */
function getRunDetailPollDelay(
  status: WorkflowRunStatus,
  suspendedResponseCount: number
): number | null {
  switch (status) {
    case 'RUNNING':
      return RUN_DETAIL_POLL_MS;
    case 'SUSPENDED':
      return suspendedResponseCount <= FAST_SUSPENDED_POLL_COUNT
        ? RUN_DETAIL_POLL_MS
        : SUSPENDED_RUN_DETAIL_POLL_MS;
    case 'COMPLETED':
    case 'FAILED':
    case 'CANCELED':
    case 'WAIT_EXPIRED':
      return null;
    default:
      console.error(`Unexpected run status: ${String(status)}`);
      return null;
  }
}

/**
 * Loads what the selected run did, or null when no run is selected. A response
 * for a run the user has already navigated away from is discarded.
 *
 * `runDetailError` describes a run that could not be loaded, which the graph
 * cannot report on its own: it has no run to paint either way. A failed fetch
 * of a run that has loaded keeps reporting that run, and a polled run is
 * polled again, unless the request got an HTTP 4xx.
 *
 * A RUNNING or SUSPENDED run is reloaded on a timer while the tab is visible
 * (see `getRunDetailPollDelay`), and at once when the tab becomes visible
 * again. `refetchRunDetail` reloads the selected run on demand.
 *
 * While `isRunViewMode` is false, nothing is fetched and the loaded run stays
 * reported. Returning to a run view mode fetches it again, without an error toast.
 *
 * `runStatusChangeCount` goes up each time a response for the selected run has
 * a different status from the run's previous response. A run's first response
 * is not a change.
 */
export function useWorkflowRunDetail(
  workflowId: string,
  runId: string | null,
  isRunViewMode: boolean
): {
  runDetail: WorkflowRunHistoryDetail | null;
  runDetailError: string | null;
  runStatusChangeCount: number;
  refetchRunDetail: () => void;
} {
  const { getToken } = useToken();
  const [runDetail, setRunDetail] = useState<WorkflowRunHistoryDetail | null>(
    null
  );
  const [previousRender, setPreviousRender] = useState({
    runId,
    isRunViewMode,
  });
  if (
    previousRender.runId !== runId ||
    previousRender.isRunViewMode !== isRunViewMode
  ) {
    // Drops the loaded run if a different run was selected outside the run view modes. The canvas
    // would otherwise draw the old run until the new run's response arrives.
    if (!previousRender.isRunViewMode && previousRender.runId !== runId) {
      setRunDetail(null);
    }
    setPreviousRender({ runId, isRunViewMode });
  }
  const [runDetailError, setRunDetailError] = useState<string | null>(null);
  const [isPageVisible, setIsPageVisible] = useState(
    () => document.visibilityState === 'visible'
  );
  const selectedRunId = useRef<string | null>(runId);
  // The selected run's consecutive SUSPENDED responses, which set the poll rate.
  const suspendedResponseCount = useRef(0);
  // The status in the selected run's last response, or null before one arrives. A failed fetch
  // keeps a loaded run, and a new status counts as a change.
  const loadedRunStatus = useRef<WorkflowRunStatus | null>(null);
  // How many times a response for the selected run has reported a new status.
  const [runStatusChangeCount, setRunStatusChangeCount] = useState(0);
  // Fetch failures that kept the loaded run. Each one re-runs the poll effect, which would
  // otherwise wait for a new `runDetail` that never comes.
  const [failedPollCount, setFailedPollCount] = useState(0);

  // Fetches the selected run. Automatic fetches set `silentErrors`, so a failing server doesn't
  // toast on every poll.
  const fetchRunDetail = useCallback(
    ({ silentErrors }: { silentErrors: boolean }) => {
      if (selectedRunId.current !== runId) {
        suspendedResponseCount.current = 0;
        loadedRunStatus.current = null;
      }
      selectedRunId.current = runId;
      setRunDetailError(null);
      if (runId === null) {
        setRunDetail(null);
        return;
      }
      const token = getToken();
      if (token === null) {
        // Report a terminal state
        loadedRunStatus.current = null;
        setRunDetail(null);
        setRunDetailError('Failed to load this run.');
        return;
      }
      getWorkflowHistory(token, workflowId, runId, { silentErrors })
        .then((detail) => {
          if (detail.id === selectedRunId.current) {
            if (detail.status === 'SUSPENDED') {
              suspendedResponseCount.current += 1;
            } else {
              suspendedResponseCount.current = 0;
            }
            if (
              loadedRunStatus.current !== null &&
              loadedRunStatus.current !== detail.status
            ) {
              setRunStatusChangeCount((count) => count + 1);
            }
            loadedRunStatus.current = detail.status;
            setRunDetail(detail);
          }
        })
        .catch((error) => {
          if (runId === selectedRunId.current) {
            console.error('Failed to load run detail:', error);
            // A failure other than a refusal may pass, so the loaded run stays and is polled again.
            if (loadedRunStatus.current !== null && !isRefusedRequest(error)) {
              setFailedPollCount((count) => count + 1);
            } else {
              loadedRunStatus.current = null;
              setRunDetail(null);
              setRunDetailError('Failed to load this run.');
            }
          }
        });
    },
    [workflowId, runId, getToken]
  );

  // Reloads the selected run on demand, toasting if it fails.
  const refetchRunDetail = useCallback(() => {
    fetchRunDetail({ silentErrors: false });
  }, [fetchRunDetail]);

  // Fetches the selected run when it is selected and on each return to a run view mode. A loaded run
  // stays on screen if the fetch fails, so the failure doesn't toast.
  useEffect(() => {
    if (!isRunViewMode) {
      setRunDetailError(null);
      return;
    }
    const isRunLoaded =
      selectedRunId.current === runId && loadedRunStatus.current !== null;
    fetchRunDetail({ silentErrors: isRunLoaded });
  }, [fetchRunDetail, isRunViewMode, runId]);

  // Polls the selected run after each response, at the delay its status sets.
  // Nothing polls while the tab is hidden or outside the run view modes.
  useEffect(() => {
    if (
      !isRunViewMode ||
      !isPageVisible ||
      runDetail === null ||
      runDetail.id !== runId
    ) {
      return;
    }
    const delay = getRunDetailPollDelay(
      runDetail.status,
      suspendedResponseCount.current
    );
    if (delay === null) {
      return;
    }
    const timer = window.setTimeout(() => {
      fetchRunDetail({ silentErrors: true });
    }, delay);
    return () => {
      window.clearTimeout(timer);
    };
  }, [
    isRunViewMode,
    isPageVisible,
    runDetail,
    runId,
    fetchRunDetail,
    failedPollCount,
  ]);

  // Tracks whether the tab is visible. Returning to the tab fetches a run that
  // is still RUNNING or SUSPENDED at once, so it does not show what it did
  // before the tab was hidden until the next poll.
  useEffect(() => {
    /** Records the tab's visibility, and fetches a polled run when it becomes visible. */
    function handleVisibilityChange(): void {
      const isVisible = document.visibilityState === 'visible';
      setIsPageVisible(isVisible);
      if (
        isVisible &&
        isRunViewMode &&
        runDetail !== null &&
        runDetail.id === runId &&
        getRunDetailPollDelay(
          runDetail.status,
          suspendedResponseCount.current
        ) !== null
      ) {
        fetchRunDetail({ silentErrors: true });
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isRunViewMode, runDetail, runId, fetchRunDetail]);

  return { runDetail, runDetailError, runStatusChangeCount, refetchRunDetail };
}
